/**
 * Derives display identity for the logged-in admin from the Firestore user
 * doc stored in redux (`state.userDataSlice.userData`). Handles the common
 * placeholder values ("N/A", empty) and falls back to the email handle.
 */

const cleanName = (value) => {
  const v = (value || "").trim();
  if (!v || v.toUpperCase() === "N/A") return "";
  return v;
};

// Turn an email handle like "jane.doe" into "Jane Doe".
const handleFromEmail = (email) => {
  const handle = (email || "").split("@")[0];
  if (!handle) return "";
  return handle
    .replace(/[._-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export const getDisplayName = (user) =>
  cleanName(user?.name) || handleFromEmail(user?.email) || "Admin";

export const getFirstName = (user) => getDisplayName(user).split(" ")[0];

export const getRoleLabel = (user) => {
  const role = (user?.role || "").toLowerCase();
  if (role === "admin") return "Administrator";
  if (role) return role.charAt(0).toUpperCase() + role.slice(1);
  return "2nd Home";
};

export const getInitial = (user) =>
  getDisplayName(user).charAt(0).toUpperCase() || "A";

// Up-to-two-letter initials for an avatar ("Jane Doe" → "JD"). Accepts a user
// object or a plain display-name string.
export const getInitials = (nameOrUser) => {
  const name =
    typeof nameOrUser === "string" ? nameOrUser : getDisplayName(nameOrUser);
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

// Single source of truth for "may this user access the admin dashboard".
// Used by both the login gate and the route guard so they can't drift.
export const isAdminUser = (user) => user?.role === "admin";

// Single source of truth for "may this user be assigned an inspection":
// anyone who finished their mobile profile (isCompleted "2") — admins
// included, since they can do inspections too. Used by the assign modal +
// the scheduler so they can't drift.
export const isAssignableInspector = (user) =>
  user?.isCompleted === "2";

// Display label for a user in admin lists. Single fallback path used
// everywhere: Name -> Email -> ID (-> "Unknown" only if all are missing).
// Whitespace-only names and the "N/A" placeholder count as "no name".
export const getUserLabel = (user) =>
  cleanName(user?.name) ||
  (user?.email || "").trim() ||
  user?._id ||
  user?.uuid ||
  "Unknown";
